import * as THREE from 'three';

/**
 * SceneSlicer class handles cutting 3D models with a clipping plane
 * to view cross-sections of the model
 */
export class SceneSlicer {
    private scene: THREE.Scene;
    private renderer: THREE.WebGLRenderer;
    private clippingPlane: THREE.Plane;
    private planeHelper: THREE.PlaneHelper;
    private isEnabled: boolean = false;

    // For drag controls
    private planeObject: THREE.Mesh;
    private dragControls: any;
    private camera: THREE.Camera;

    // Position and normal
    private planePosition: THREE.Vector3;
    private planeNormal: THREE.Vector3;

    constructor(scene: THREE.Scene, renderer: THREE.WebGLRenderer, camera: THREE.Camera) {
        this.scene = scene;
        this.renderer = renderer;
        this.camera = camera;

        // Initialize with a default plane position (center of scene) and normal (along X axis)
        this.planePosition = new THREE.Vector3(0, 0, 0);
        this.planeNormal = new THREE.Vector3(1, 0, 0);

        // Create the clipping plane
        this.clippingPlane = new THREE.Plane(this.planeNormal, 0);

        // Create a visual helper for the plane
        this.planeHelper = new THREE.PlaneHelper(this.clippingPlane, 1000, 0x00ff00);
        this.planeHelper.visible = false;
        this.markAsHelper(this.planeHelper);
        this.scene.add(this.planeHelper);

        // Create a visible and draggable representation of the plane
        this.createDraggablePlane();
    }

    /**
     * Recursively mark an object and all its children as helpers
     */
    private markAsHelper(object: THREE.Object3D): void {
        object.userData.isHelper = true;
        object.traverse((child) => {
            child.userData.isHelper = true;
        });
    }

    /**
     * Create a draggable plane object for user interaction
     */
    private createDraggablePlane(): void {
        const planeGeometry = new THREE.PlaneGeometry(1000, 1000);
        const planeMaterial = new THREE.MeshBasicMaterial({
            color: 0x00ff00,
            side: THREE.DoubleSide,
            transparent: true,
            opacity: 0.2,
            depthWrite: false
        });

        this.planeObject = new THREE.Mesh(planeGeometry, planeMaterial);
        this.planeObject.position.copy(this.planePosition);
        this.planeObject.lookAt(this.planePosition.clone().add(this.planeNormal));
        this.planeObject.userData.isSlicerPlane = true;
        this.markAsHelper(this.planeObject);
        this.planeObject.visible = false;
        this.scene.add(this.planeObject);
    }

    /**
     * Enable the slicer
     */
    public enable(): void {
        if (this.isEnabled) return;

        this.isEnabled = true;
        this.planeHelper.visible = true;
        this.planeObject.visible = true;

        // Enable clipping in the renderer
        this.renderer.localClippingEnabled = true;

        // Update all materials in the scene to use clipping
        this.updateMaterialsForClipping(true);
    }

    /**
     * Disable the slicer
     */
    public disable(): void {
        if (!this.isEnabled) return;

        this.isEnabled = false;
        this.planeHelper.visible = false;
        this.planeObject.visible = false;

        // Disable clipping in the renderer if no other features need it
        this.renderer.localClippingEnabled = false;

        // Update all materials in the scene to not use clipping
        this.updateMaterialsForClipping(false);
    }

    /**
     * Toggle the slicer on/off
     */
    public toggle(): void {
        console.log("Toggling slicer. Current state:", this.isEnabled);

        if (this.isEnabled) {
            this.disable();
        } else {
            // Force update of scene center before enabling
            this.centerOnScene();
            this.enable();
        }

        // Force a refresh of the renderer
        this.renderer.setRenderTarget(null);

        console.log("Slicer toggled. New state:", this.isEnabled);
    }

    /**
     * Update the clipping plane position
     * @param position New position vector
     */
    public setPosition(position: THREE.Vector3): void {
        this.planePosition = position.clone();
        this.updatePlane();
    }

    /**
     * Update the clipping plane normal
     * @param normal New normal vector
     */
    public setNormal(normal: THREE.Vector3): void {
        this.planeNormal = normal.clone().normalize();
        this.updatePlane();
    }

    /**
     * Set the orientation of the clipping plane to a specific axis
     * @param axis The axis to align with ('x', 'y', or 'z')
     * @param negative Whether to use the negative direction of the axis
     */
    public setAxis(axis: 'x' | 'y' | 'z', negative: boolean = false): void {
        let normal = new THREE.Vector3();

        switch (axis) {
            case 'x':
                normal.set(1, 0, 0);
                break;
            case 'y':
                normal.set(0, 1, 0);
                break;
            case 'z':
                normal.set(0, 0, 1);
                break;
        }

        if (negative) {
            normal.negate();
        }

        this.setNormal(normal);
    }

    /**
     * Move the plane along its normal direction
     * @param distance Distance to move (can be negative to move in the opposite direction)
     */
    public movePlane(distance: number): void {
        const moveVector = this.planeNormal.clone().multiplyScalar(distance);
        this.planePosition.add(moveVector);
        this.updatePlane();
    }

    /**
     * Update the clipping plane and its visual representations
     */
    private updatePlane(): void {
        if (!this.isEnabled) return;

        // Calculate the constant for the plane equation ax + by + cz + d = 0
        // where (a,b,c) is the normal and d is the negative dot product of normal and position
        const constant = -this.planeNormal.dot(this.planePosition);

        // Update the clipping plane
        this.clippingPlane.set(this.planeNormal, constant);

        // Update the visual helpers
        this.scene.remove(this.planeHelper);
        this.planeHelper = new THREE.PlaneHelper(this.clippingPlane, 1000, 0x00ff00);
        this.planeHelper.visible = this.isEnabled;
        this.markAsHelper(this.planeHelper);
        this.scene.add(this.planeHelper);

        // Update the draggable plane
        this.planeObject.position.copy(this.planePosition);
        this.planeObject.lookAt(this.planePosition.clone().add(this.planeNormal));
    }

    /**
     * Update all materials in the scene to enable/disable clipping
     * @param enableClipping Whether to enable or disable clipping
     */
    private updateMaterialsForClipping(enableClipping: boolean): void {
        console.log(`Updating materials for clipping: ${enableClipping}`);

        // Force enable clipping in the renderer
        this.renderer.localClippingEnabled = enableClipping;

        this.scene.traverse((node) => {
            if (node instanceof THREE.Mesh) {
                if (Array.isArray(node.material)) {
                    // For multi-material objects
                    node.material.forEach(material => {
                        this.updateMaterialClipping(material, enableClipping);
                    });
                } else if (node.material) {
                    // For single material objects
                    this.updateMaterialClipping(node.material, enableClipping);
                }
            }
        });

        console.log("Material update complete");
    }

    /**
     * Helper to update a single material's clipping properties
     * @param material The material to update
     * @param enableClipping Whether to enable or disable clipping
     */
    private updateMaterialClipping(material: THREE.Material, enableClipping: boolean): void {
        if (!material) return;

        if (enableClipping) {
            // Set the clipping planes on the material
            material.clippingPlanes = [this.clippingPlane];

            // Enable side setting for better visual results
            if ('side' in material) {
                material.side = THREE.DoubleSide;
            }

            // Different material types need different properties
            if (material instanceof THREE.MeshStandardMaterial ||
                material instanceof THREE.MeshPhongMaterial ||
                material instanceof THREE.MeshBasicMaterial ||
                material instanceof THREE.MeshLambertMaterial) {

                material.clipShadows = true;
                material.needsUpdate = true;
            }
        } else {
            // Remove clipping planes
            material.clippingPlanes = [];

            // Restore default settings
            if ('side' in material) {
                material.side = THREE.FrontSide;
            }

            if (material instanceof THREE.MeshStandardMaterial ||
                material instanceof THREE.MeshPhongMaterial ||
                material instanceof THREE.MeshBasicMaterial ||
                material instanceof THREE.MeshLambertMaterial) {

                material.clipShadows = false;
                material.needsUpdate = true;
            }
        }

        // Force material update
        material.needsUpdate = true;
    }

    /**
     * Reset the clipping plane to default position and orientation
     */
    public reset(): void {
        // Center the plane at the origin with normal along X axis
        this.planePosition = new THREE.Vector3(0, 0, 0);
        this.planeNormal = new THREE.Vector3(1, 0, 0);
        this.updatePlane();
    }

    /**
     * Check if the slicer is currently enabled
     */
    public isActive(): boolean {
        return this.isEnabled;
    }

    /**
     * Get the current clipping plane
     */
    public getClippingPlane(): THREE.Plane {
        return this.clippingPlane;
    }

    /**
     * Get the current plane position
     */
    public getPlanePosition(): THREE.Vector3 {
        return this.planePosition.clone();
    }

    /**
     * Get the current plane normal
     */
    public getPlaneNormal(): THREE.Vector3 {
        return this.planeNormal.clone();
    }

    /**
     * Position the slicing plane automatically to slice through the center of a specific object
     * @param object The object to center the slice on
     */
    public centerOnObject(object: THREE.Object3D): void {
        if (!object) return;

        // Calculate the object's bounding box
        const boundingBox = new THREE.Box3().setFromObject(object);
        const center = boundingBox.getCenter(new THREE.Vector3());

        // Set the plane position to the center of the object
        this.setPosition(center);
    }

    /**
     * Center the slicing plane on the entire scene
     */
    public centerOnScene(): void {
        console.log("Centering slice plane on scene");

        // Calculate the scene's bounding box
        const boundingBox = new THREE.Box3();
        let objectCount = 0;

        // Exclude helpers and the slice plane itself
        this.scene.traverse((node) => {
            if (node instanceof THREE.Mesh &&
                !node.userData.isHelper &&
                !node.userData.isSlicerPlane) {
                boundingBox.expandByObject(node);
                objectCount++;
            }
        });

        if (objectCount === 0) {
            console.warn("No objects found in scene to center on!");
            // Default to scene origin if no objects found
            this.setPosition(new THREE.Vector3(0, 0, 0));
            return;
        }

        const center = boundingBox.getCenter(new THREE.Vector3());
        console.log("Scene center:", center);

        // Set the plane position to the center of the scene
        this.setPosition(center);
    }

    /**
     * Get the scene reference
     */
    public getScene(): THREE.Scene {
        return this.scene;
    }

    /**
     * Get the scene bounds (excluding helpers and slicer plane)
     */
    public getSceneBounds(): THREE.Box3 {
        const boundingBox = new THREE.Box3();
        let objectCount = 0;

        this.scene.traverse((node) => {
            if (node instanceof THREE.Mesh &&
                !node.userData.isHelper &&
                !node.userData.isSlicerPlane) {
                boundingBox.expandByObject(node);
                objectCount++;
            }
        });

        if (objectCount === 0) {
            // Return a default box if no objects found
            boundingBox.setFromCenterAndSize(new THREE.Vector3(0, 0, 0), new THREE.Vector3(100, 100, 100));
        }

        return boundingBox;
    }
} 